class EffortlessPowerSection {
  static CONSTANTS = {
    NUMBER_ANIMATION_DURATION: 1000,
    NUMBER_CYCLES: 3,
    FEATURE_STAGGER_DELAY: 200
  };

  constructor(section) {
    this.section = section;
    this.numberElements = this.section.querySelectorAll('.effortless-power-section__stat-number');
    this.statsElement = this.section.querySelector('.effortless-power-section__stats');
    this.mediaElement = this.section.querySelector('[data-animate="media"]');
    this.featureElements = this.section.querySelectorAll('[data-animate="feature"]');
    this.featuresContainer = this.section.querySelector('.effortless-power-section__features');
    this.effortlessTitles = this.section.querySelectorAll('.effortless-power-section__title');
    this.animated = false;
    this.mediaAnimated = false;
    this.featuresAnimated = false;
    this.init();
  }

  init() {
    this.setupOdometerNumbers();
    this.setupIntersectionObserver();
    this.setupGsapAnimation();
  }

  setupOdometerNumbers() {
    this.numberElements.forEach(element => {
      const target = element.dataset.target;
      if (!this.isValidTarget(target)) return;
      const digits = target.toString().split('');
      element.textContent = '';
      digits.forEach(digit => {
        const wrapper = this.createDigitWrapper(digit);
        element.appendChild(wrapper);
      });
    });
  }

  isValidTarget(target) {
    return target && target !== '' && target !== '0' && parseInt(target) !== 0;
  }

  createDigitWrapper(digit) {
    const wrapper = document.createElement('span');
    wrapper.className = 'effortless-power-section__digit-wrapper';
    wrapper.appendChild(this.createDigitColumn(digit));
    return wrapper;
  }

  createDigitColumn(digit) {
    const column = document.createElement('span');
    column.className = 'effortless-power-section__digit-column';
    column.dataset.target = digit;
    const targetDigit = parseInt(digit);
    const { NUMBER_CYCLES } = EffortlessPowerSection.CONSTANTS;
    const maxDigit = (NUMBER_CYCLES * 10) + targetDigit;
    for (let i = 0; i <= maxDigit; i++) {
      const digitSpan = document.createElement('span');
      digitSpan.className = 'effortless-power-section__digit';
      digitSpan.textContent = i % 10;
      column.appendChild(digitSpan);
    }
    column.style.willChange = 'transform';
    return column;
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.animateAll();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (entry.target === this.statsElement && !this.animated) {
          this.animated = true;
          this.animateNumbers();
          setTimeout(() => this.animateStats(), 50);
        } else if (entry.target === this.mediaElement && !this.mediaAnimated) {
          this.mediaAnimated = true;
          this.animateMedia();
        } else if (entry.target === this.featuresContainer && !this.featuresAnimated) {
          this.featuresAnimated = true;
          this.animateFeatures();
        }
      });
    }, { root: null, rootMargin: '0px', threshold: [0.1, 0.2] });

    if (this.statsElement) this.observer.observe(this.statsElement);
    if (this.mediaElement) this.observer.observe(this.mediaElement);
    if (this.featuresContainer) this.observer.observe(this.featuresContainer);
  }

  animateAll() {
    this.animateStats();
    this.animateNumbers();
    this.animateMedia();
    this.animateFeatures();
  }

  animateNumbers() {
    this.numberElements.forEach(element => {
      const target = element.dataset.target;
      if (!this.isValidTarget(target)) return;
      element.querySelectorAll('.effortless-power-section__digit-column').forEach(column => {
        this.animateDigitColumn(column, parseInt(column.dataset.target));
      });
    });
  }

  animateDigitColumn(column, targetDigit) {
    const digitEl = column.querySelector('.effortless-power-section__digit');
    if (!digitEl) return;
    const digitHeight = digitEl.offsetHeight;
    const { NUMBER_CYCLES, NUMBER_ANIMATION_DURATION } = EffortlessPowerSection.CONSTANTS;
    const translateY = -((NUMBER_CYCLES * 10 + targetDigit) * digitHeight);
    column.style.transition = 'none';
    column.style.transform = 'translate3d(0,0,0)';
    column.getBoundingClientRect();
    column.style.transition = `transform ${NUMBER_ANIMATION_DURATION}ms cubic-bezier(0.6, 0, 0.2, 1)`;
    requestAnimationFrame(() => {
      column.style.transform = `translate3d(0, ${translateY}px, 0)`;
    });
  }

  animateStats() {
    if (this.statsElement) this.statsElement.classList.add('is-visible');
  }

  animateMedia() {
    if (this.mediaElement) this.mediaElement.classList.add('is-visible');
  }

  animateFeatures() {
    this.featureElements.forEach((feature, index) => {
      setTimeout(() => feature.classList.add('is-visible'), index * EffortlessPowerSection.CONSTANTS.FEATURE_STAGGER_DELAY);
    });
  }

  setupGsapAnimation() {
    const initGsap = () => {
      if (typeof gsap !== 'undefined') {
        if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);
        this.effortlessTitles.forEach(title => this.setupSplitForTitle(title));
        clearInterval(this.gsapCheckInterval);
      }
    };
    initGsap();
    if (typeof gsap === 'undefined') {
      this.gsapCheckInterval = setInterval(initGsap, 500);
      setTimeout(() => clearInterval(this.gsapCheckInterval), 5000);
    }
  }

  setupSplitForTitle(title) {
    if (title.anim) {
      title.anim.progress(1).kill();
      if (title.split) title.split.revert();
    }
    if (typeof SplitText !== 'undefined') {
      title.split = new SplitText(title, { type: "words,chars", linesClass: "split-line" });
    } else {
      this.splitTextManually(title);
    }
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;

    gsap.set(chars, { y: 80, opacity: 0 });
    this.updateCharGradients(title);
    title.dataset.gsapReady = 'true';
    this.setupTitleObserver(title);
  }

  splitTextManually(element) {
    if (element.querySelector('.char')) return;
    const text = element.innerHTML;
    const lines = text.split('<br>');
    element.innerHTML = '';
    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) element.appendChild(document.createElement('br'));
      line.trim().split(/(\s+)/).forEach(word => {
        if (word.match(/\s/)) {
          element.appendChild(document.createTextNode(word));
          return;
        }
        word.split('').forEach(char => {
          const span = document.createElement('span');
          span.className = 'char';
          span.textContent = char;
          element.appendChild(span);
        });
      });
    });
  }

  setupTitleObserver(title) {
    title.dataset.hasTriggered = 'false';
    let lastScrollY = window.scrollY || window.pageYOffset;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const currentScrollY = window.scrollY || window.pageYOffset;
        const isScrollingDown = currentScrollY > lastScrollY;
        lastScrollY = currentScrollY;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.15 && title.dataset.hasTriggered === 'false') {
          title.dataset.hasTriggered = 'true';
          if (isScrollingDown) {
            this.animateTitleChars(title);
          } else {
            const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
            if (chars && chars.length > 0) gsap.set(chars, { y: 0, opacity: 1 });
          }
        } else if (!entry.isIntersecting && entry.intersectionRatio < 0.05 && title.dataset.hasTriggered === 'true') {
          const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
          if (chars && chars.length > 0) gsap.set(chars, { y: 80, opacity: 0 });
          title.dataset.hasTriggered = 'false';
        }
      });
    }, {
      root: null,
      rootMargin: '50px 0px 50px 0px',
      threshold: [0, 0.1, 0.15, 0.2, 0.3, 0.5]
    });

    observer.observe(title);
    title.observer = observer;

    requestAnimationFrame(() => {
      const rect = title.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.top < windowHeight * 0.85 && rect.bottom > 0 && title.dataset.hasTriggered === 'false') {
        const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
        if (chars && chars.length > 0) {
          gsap.set(chars, { y: 0, opacity: 1 });
          title.dataset.hasTriggered = 'true';
        }
      }
    });
  }

  animateTitleChars(title) {
    if (!title.dataset.gsapReady || typeof gsap === 'undefined') return;
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;
    if (title.anim) title.anim.progress(1).kill();

    title.anim = gsap.fromTo(chars,
      { y: 80, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.6, ease: 'circ.out', stagger: 0.02,
        onComplete: () => this.updateCharGradients(title)
      }
    );
  }

  updateCharGradients(title) {
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;
    const gradient = 'linear-gradient(114deg, #0068FF 33.4%, #000 64.14%)';
    const titleRect = title.getBoundingClientRect();
    const fullWidth = titleRect.width;
    chars.forEach(char => {
      const charRect = char.getBoundingClientRect();
      const offsetX = charRect.left - titleRect.left;
      char.style.background = gradient;
      char.style.backgroundSize = `${fullWidth}px 100%`;
      char.style.backgroundPosition = `-${offsetX}px 0`;
      char.style.backgroundClip = 'text';
      char.style.webkitBackgroundClip = 'text';
      char.style.webkitTextFillColor = 'transparent';
    });
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.gsapCheckInterval) clearInterval(this.gsapCheckInterval);
    this.effortlessTitles.forEach(title => {
      if (title.observer) title.observer.disconnect();
      if (title.anim) title.anim.kill();
      if (title.split) title.split.revert();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.effortless-power-section').forEach(section => {
    if (!section.__effortless_inited__) {
      section.__effortless_inited__ = true;
      section.__effortless_instance__ = new EffortlessPowerSection(section);
    }
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.effortless-power-section');
    if (section && !section.__effortless_inited__) {
      section.__effortless_inited__ = true;
      section.__effortless_instance__ = new EffortlessPowerSection(section);
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.effortless-power-section');
    if (section && section.__effortless_instance__) {
      section.__effortless_instance__.destroy();
      section.__effortless_inited__ = false;
      section.__effortless_instance__ = null;
    }
  });
}
